import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import InvoiceGalleryMarquee from './components/InvoiceGalleryMarquee';
import InvoiceDesignForm from './components/InvoiceDesignForm';

export default function CustomInvoiceDesignExperiment() {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 animate-fade-in-up">
            Request a Custom Invoice Design
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto animate-fade-in-up">
            <span className="block mb-3" style={{ color: '#0089D8' }}>
              Use the Xero biomail professional invoice layout with your own preferred image header and page colours for Free.
            </span>
            <span className="block mb-3">
              Or would you prefer to create an invoice layout that matches your brand perfectly?
            </span>
            <span className="block">
              Tell us what you have in mind and our design team will craft a bespoke layout just for your
              business for a one time standard nominal design and setup fee.
            </span>
          </p>
        </div>
      </section>

      {/* Main Content — Invoice Image on Left, Content on Right */}
      <section className="relative">
        <div className="flex">
          {/* Left: Invoice Background Image */}
          <div className="hidden lg:block w-[380px] xl:w-[420px] 2xl:w-[460px] flex-shrink-0">
            <div className="sticky top-16 px-6 pt-16">
              <h3
                className="text-3xl italic mb-3 text-center"
                style={{ color: '#29B3E6', fontFamily: "'Carlito', 'Calibri', sans-serif" }}
              >
                <span style={{ fontWeight: 'bold' }}>Free</span> professional bioMail Invoice
              </h3>
              <img
                src="https://storage.helloreaddy.io/project_files/d29f47c9-44c4-419b-bc6f-99193685fe87/538d68b3-78ec-40fb-b5a7-8f5c5b57eeff_compressed_fahey_graded_invoice.webp"
                alt="Fahey Quality Painters Invoice Example"
                className="w-full rounded-xl border border-gray-200 mt-[18px]"
              />
            </div>
          </div>

          {/* Right: All Content */}
          <div className="flex-1 min-w-0">
            {/* Invoice Gallery Marquee */}
            <div className="py-16 border-b border-gray-100">
              <div className="px-6 lg:px-8 mb-10 text-center">
                <h2 className="text-3xl font-bold mb-3" style={{ color: '#29B3E6' }}>
                  Explore. Choose. Create.
                </h2>
                <p className="text-gray-600 whitespace-nowrap">
                  A glimpse at some of the styles we can create — from clean and minimal to bold and branded — create your own.
                </p>
              </div>
              <div className="overflow-hidden">
                <InvoiceGalleryMarquee />
              </div>
            </div>

            {/* Form Section */}
            <div className="py-20 bg-gray-50">
              <div className="px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
                  {/* Left: info */}
                  <div className="lg:sticky lg:top-28">
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">
                      Let's design something great
                    </h2>
                    <p className="text-gray-600 mb-8">
                      Just share a few details and we'll get back to you. No commitment required —
                      it's the first step to a layout that's uniquely yours.
                    </p>

                    <div className="space-y-6">
                      {[
                        {
                          icon: 'ri-mail-send-line',
                          title: 'Tell us what you need',
                          text: 'Share your details and, if you have one, an example of your current design.',
                        },
                        {
                          icon: 'ri-palette-line',
                          title: 'We craft a draft',
                          text: 'Our team designs a bespoke invoice layout based on your brand and requirements.',
                        },
                        {
                          icon: 'ri-loop-right-line',
                          title: 'Refine together',
                          text: 'We iterate with you until it looks exactly the way you want.',
                        },
                        {
                          icon: 'fa-solid fa-dollar-sign',
                          title: 'Design and Development',
                          text: (
                            <>
                              <span style={{ color: '#29B3E6' }}>
                                A one time only design and development fee of US $265 applies.
                              </span>{' '}
                              This covers up to 5 iterations of your design to fine tune to your requirements.
                            </>
                          ),
                        },
                      ].map((step) => (
                        <div key={step.title} className="flex items-start gap-4">
                          <div className="w-11 h-11 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <i className={`${step.icon} text-blue-600 text-lg w-5 h-5 flex items-center justify-center`}></i>
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900 mb-1">{step.title}</h3>
                            <p className="text-gray-600 text-sm">{step.text}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-10 p-5 bg-white rounded-xl border border-gray-200">
                      <p className="text-sm text-gray-500 mb-1">Prefer to email us directly?</p>
                      <a
                        href="mailto:sales@bioverifier.com"
                        className="text-blue-600 hover:text-blue-700 transition-colors font-medium text-sm"
                      >
                        sales@bioverifier.com
                      </a>
                    </div>
                  </div>

                  {/* Right: form */}
                  <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8">
                    <InvoiceDesignForm />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}